import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { Analyst } from '@analysis/domain';
import { distinctUntilChanged, filter } from 'rxjs/operators';

/** Where the view is, as soon as the route says so. Independent of any analyst. */
export interface SampleLocation {
  caseId: string;
  sampleId: string;
}

/**
 * What the view is showing, once it is coherent AND settled: the location plus the
 * analyst that was actually resolved for it — never the outgoing one.
 *
 * `generation` is the key every per-analyst read is scoped by. Two reads with the same
 * generation are interchangeable; a result carrying a different generation than the
 * current one is stale and must not be applied.
 */
export interface AnalysisViewContext extends SampleLocation {
  analyst: Analyst;
  generation: string;
}

export function viewGeneration(sampleId: string, analystId: string): string {
  return `${sampleId}:${analystId}`;
}

interface ViewContextState {
  location: SampleLocation | null;
  context: AnalysisViewContext | null;
}

/**
 * The single answer to "what is on screen right now".
 *
 * Every per-sample read in the analysis views used to reconstruct that answer for itself,
 * by pairing whatever event it listened to with `concatLatestFrom(currentState)` at the
 * moment it happened to fire — so an event could be paired with a different generation
 * than the one it belonged to, and nothing validated a result before applying it. This
 * store makes the pair explicit and gives it one writer.
 *
 * Deliberately materialised rather than left as a derived selector chain: a wrong
 * generation becomes one value you can read in a test or in devtools, and consumers are
 * not exposed to warm-up hazards (`concatLatestFrom` silently drops an emission when a
 * cold or debounced selector has not published yet).
 *
 * Written ONLY by `SampleViewLoadingEffects`. It derives — it authors nothing: the route
 * and the analyst resolution stay authoritative.
 */
@Injectable()
export class ViewContextStore extends ComponentStore<ViewContextState> {
  constructor() {
    super({ location: null, context: null });
  }

  /**
   * Emits as soon as the route settles, WITHOUT waiting for an analyst. Reads that do not
   * depend on who analysed the sample (its assets, the analysis states of everyone on it)
   * are keyed on this, so they are not delayed by an analyst round-trip.
   */
  public readonly location$ = this.select((state) => state.location).pipe(
    filter((location): location is SampleLocation => !!location),
    distinctUntilChanged((a, b) => a.caseId === b.caseId && a.sampleId === b.sampleId)
  );

  /** Emits once per coherent, settled generation. The key for every per-analyst read. */
  public readonly context$ = this.select((state) => state.context).pipe(
    filter((context): context is AnalysisViewContext => !!context),
    distinctUntilChanged((a, b) => a.generation === b.generation)
  );

  public readonly generation$ = this.select((state) => state.context?.generation ?? null);

  /** Synchronous read, for validating a result that is about to be applied. */
  public get currentGeneration(): string | null {
    return this.get((state) => state.context?.generation ?? null);
  }

  public readonly setLocation = this.updater((state, location: SampleLocation) => ({
    ...state,
    location,
  }));

  public readonly setContext = this.updater((state, context: AnalysisViewContext) => ({
    ...state,
    context,
  }));
}
